import { useTranslation } from "@sagot/i18n";
import { Button, Dialog, DialogContent, ErrorState, Field, Icon, Input, RadioGroup, Select, Skeleton, toast } from "@sagot/ui";
import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import { DefaultWorkflowNote } from "../workflows/defaults/default-note";
import { useMembers } from "../../../roles/use-roles";
import type { LeadRouting, LeadRoutingSettings } from "./lead-types";
import { ROUTING_MODES, routingFromSettings, routingSchema, toRoutingUpdate, type RoutingValues } from "./routing-model";
import { useLeadRouting, useSaveLeadRouting } from "./use-lead-routing";

const P = "engage.routing";
const FORM_ID = "lead-routing-form";

interface RoutingFormProps {
  settings: LeadRoutingSettings;
  canEdit: boolean;
  save: ReturnType<typeof useSaveLeadRouting>;
  onSaved: () => void;
}

function RoutingForm({ settings, canEdit, save, onSaved }: RoutingFormProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const members = useMembers();
  const schema = routingSchema({ ownerRequired: t(`${P}.owner.required`), emailInvalid: t(`${P}.email.invalid`) });
  const form = useForm<RoutingValues>({ resolver: zodResolver(schema), defaultValues: routingFromSettings(settings) });
  const routing = useWatch({ control: form.control, name: "routing" });
  const active = (members.data ?? []).filter((member) => member.status === "active");
  const knowsOwner = active.some((member) => member.user_id === settings.default_owner_user_id);
  const submit = form.handleSubmit((values) => {
    save.mutate(toRoutingUpdate(values), {
      onSuccess: () => {
        toast.success(t(`${P}.saved`));
        onSaved();
      },
    });
  });
  return (
    <form id={FORM_ID} noValidate className="space-y-5" onSubmit={(event) => void submit(event)}>
      {save.isError ? <FormAlert title={message(save.error)} /> : null}
      {canEdit ? null : <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${P}.readOnly`)}</p>}
      <DefaultWorkflowNote systemKey="new_lead_route_and_alert" textKey="engage.workflows.defaults.cross.leadsRouting" />
      <div>
        <p id="routing-mode-label" className="label">{t(`${P}.mode.label`)}</p>
        <Controller
          control={form.control}
          name="routing"
          render={({ field }) => (
            <RadioGroup<LeadRouting>
              aria-labelledby="routing-mode-label"
              disabled={!canEdit}
              value={field.value}
              onValueChange={field.onChange}
              options={ROUTING_MODES.map((mode) => ({ value: mode, label: t(`${P}.mode.${mode}`), description: t(`${P}.mode.${mode}Hint`) }))}
            />
          )}
        />
      </div>
      {routing === "default_owner" ? (
        <Field label={t(`${P}.owner.label`)} error={form.formState.errors.owner?.message}>
          <Controller
            control={form.control}
            name="owner"
            render={({ field }) => (
              <Select disabled={!canEdit} {...field}>
                <option value="">{t(`${P}.owner.placeholder`)}</option>
                {!knowsOwner && settings.default_owner_user_id ? <option value={settings.default_owner_user_id}>{settings.default_owner_name ?? settings.default_owner_user_id}</option> : null}
                {active.map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.name}
                  </option>
                ))}
              </Select>
            )}
          />
        </Field>
      ) : null}
      <Field label={`${t(`${P}.email.label`)} ${t(`${P}.email.optional`)}`} hint={t(`${P}.email.hint`)} error={form.formState.errors.email?.message}>
        <Input type="email" autoComplete="off" placeholder={t(`${P}.email.placeholder`)} disabled={!canEdit} {...form.register("email")} />
      </Field>
    </form>
  );
}

interface LeadRoutingDialogProps {
  canEdit: boolean;
  onClose: () => void;
}

/** Lead routing (`GET/PUT /v1/settings/leads`): round-robin or one default owner, plus a shared leads inbox. */
export function LeadRoutingDialog({ canEdit, onClose }: LeadRoutingDialogProps) {
  const { t } = useTranslation();
  const routing = useLeadRouting(true);
  const save = useSaveLeadRouting();
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        title={t(`${P}.title`)}
        description={t(`${P}.subtitle`)}
        closeLabel={t("common.close")}
        className="max-w-lg"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
            {canEdit && routing.data ? (
              <Button type="submit" form={FORM_ID} loading={save.isPending}>
                <Icon name="check" />
                {t(`${P}.save`)}
              </Button>
            ) : null}
          </>
        }
      >
        {routing.isPending ? (
          <div className="space-y-3" aria-busy="true">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : null}
        {routing.isError ? <ErrorState headingLevel={3} title={t(`${P}.loadError`)} retryLabel={t("common.retry")} onRetry={() => void routing.refetch()} /> : null}
        {routing.data ? <RoutingForm settings={routing.data} canEdit={canEdit} save={save} onSaved={onClose} /> : null}
      </DialogContent>
    </Dialog>
  );
}

/** "Lead routing" button for the Leads header; the dialog mounts only while open. */
export function LeadRoutingButton({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" onClick={() => { setOpen(true); }}>
        <Icon name="git-branch" />
        {t(`${P}.open`)}
      </Button>
      {open ? <LeadRoutingDialog canEdit={canEdit} onClose={() => { setOpen(false); }} /> : null}
    </>
  );
}
